import { memo } from "react";
import { Box, Link, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { OpenInNewOutlined } from "@mui/icons-material";
import theme from "themeProvider";

export const DetailRow = memo(({ label, value }) => (
  <Box sx={{ display: "flex", gap: 2, py: 0.6 }}>
    <Typography
      sx={{
        fontSize: 12,
        color: theme.palette.text.secondary,
        width: 150,
        flexShrink: 0,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{
        fontSize: 12,
        fontWeight: 600,
        color: theme.palette.text.primary,
        wordBreak: "break-word",
      }}
    >
      {value ?? "-"}
    </Typography>
  </Box>
));

export const StatusPill = memo(({ label, color }) => (
  <Box
    sx={{
      display: "inline-flex",
      px: 1,
      py: 0.25,
      borderRadius: 10,
      background: alpha(color, 0.12),
    }}
  >
    <Typography
      sx={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 0.3 }}
    >
      {label}
    </Typography>
  </Box>
));

export const SectionHeader = memo(({ icon: SectionIcon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme.palette.grey[100],
        flexShrink: 0,
      }}
    >
      <SectionIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
    </Box>
    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{title}</Typography>
  </Box>
));

// One bordered "node" box per list item.
export const ListItemNode = memo(({ item, color }) => (
  <Box
    sx={{
      border: `1px solid ${theme.palette.grey[200]}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 1,
      p: 1,
    }}
  >
    <Typography
      title={item.primary || ""}
      sx={{
        fontSize: 12,
        fontWeight: 600,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        lineHeight: 1.3,
      }}
    >
      {item.primary || "-"}
    </Typography>
    <Typography
      title={item.secondary || ""}
      sx={{
        fontSize: 11,
        color: theme.palette.text.secondary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        mt: 0.25,
      }}
    >
      {item.secondary || "-"}
    </Typography>
  </Box>
));

// External link; plain text when there is no href.
export const DetailLink = memo(({ link, fontSize = 13, sx }) =>
  link.href ? (
    <Link
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, ...sx }}
    >
      {link.value}
      <OpenInNewOutlined sx={{ fontSize }} />
    </Link>
  ) : (
    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, ...sx }}>
      {link.value}
    </Typography>
  ),
);
